import { animate, style, transition, trigger } from '@angular/animations'
import { Component, Input, OnDestroy, OnInit } from '@angular/core'
import { DomSanitizer, SafeUrl } from '@angular/platform-browser'
import { ApiResult, NotificationType, ToastService, tapData } from '@ftr/foundation'
import { Store } from '@ngxs/store'
import { MultifactorAuthenticationService } from '../../services/multifactor-authentication/multifactor-authentication.service'
import { SetMfaRecoveryCodeModalOpenStatus } from '../../store'

@Component({
  selector: 'ftr-multifactor-recovery-code-modal-content',
  templateUrl: './recovery-code-modal-content.component.html',
  styleUrls: ['./recovery-code-modal-content.component.css'],
  animations: [
    trigger('closeConfirmationTrigger', [
      transition(':enter', [style({ opacity: 0 }), animate('50ms', style({ opacity: 1 }))]),
    ]),
  ],
  standalone: false,
})
export class RecoveryCodeModalContentComponent implements OnInit, OnDestroy {
  @Input() regenerate: boolean = false

  protected recoveryCodes$: ApiResult<string[]>
  protected recoveryCodesDownloadUrl: SafeUrl
  protected confirmDisplayed = false

  readonly notificationTypes = NotificationType

  private _recoveryCodes: string[]
  private _downloadUrl: string = ''

  constructor(
    private readonly store: Store,
    private readonly mfaService: MultifactorAuthenticationService,
    private readonly toastService: ToastService,
    private readonly domSanitizer: DomSanitizer,
  ) {}

  ngOnInit(): void {
    this.getRecoveryCodes()
  }

  ngOnDestroy(): void {
    window.URL.revokeObjectURL(this._downloadUrl)
  }

  getRecoveryCodes(): void {
    this.recoveryCodes$ = this.mfaService.generateRecoveryCodes(this.regenerate).pipe(
      tapData(recoveryCodes => {
        this._recoveryCodes = recoveryCodes
        this.recoveryCodesDownloadUrl = this.buildSafeCodeDownloadUrl(recoveryCodes)
      }),
    )
  }

  copyRecoveryCodes(): void {
    const allCodes = this._recoveryCodes.join(',')
    window.navigator.clipboard.writeText(allCodes)
    this.toastService.success('Copied recovery codes to clipboard.')
  }

  close(): void {
    if (this.confirmDisplayed) {
      this.confirmDisplayed = false
      this.store.dispatch(new SetMfaRecoveryCodeModalOpenStatus(false))
    } else {
      this.confirmDisplayed = true
    }
  }

  private buildSafeCodeDownloadUrl(recoveryCodes: string[]): SafeUrl {
    const textContent = recoveryCodes.join('\r\n')
    const textBlob = new Blob([textContent], { type: 'text/plain' })
    this._downloadUrl = window.URL.createObjectURL(textBlob)
    return this.domSanitizer.bypassSecurityTrustUrl(this._downloadUrl)
  }
}
